import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Badge,
  useTheme,
  useMediaQuery,
  Skeleton,
  Collapse,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import PeopleIcon from '@mui/icons-material/People';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import MailIcon from '@mui/icons-material/Mail';
import TimelineIcon from '@mui/icons-material/Timeline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import StarIcon from '@mui/icons-material/Star';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNotificationContext } from '../../hooks/NotificationContext';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../hooks/useAuth';
import { useChatHistory } from '../../hooks/useChatHistory';
import { formatRelativeTime } from '../../utils/dateUtils';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  variant?: 'permanent' | 'persistent' | 'temporary';
  currentView?: 'welcome' | 'chat'; // 現在の表示状態を追加
  title?: string; // ヘッダーから移動するタイトル
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  divider?: boolean;
  adminOnly?: boolean;
  studentOnly?: boolean;
  badge?: number;
}

const menuItems: MenuItem[] = [
  {
    id: 'chat',
    label: 'AI学習チャット',
    icon: <ChatIcon />,
    path: '/chat',
    divider: true,
  },
  // 学生向け就職活動機能（Phase2追加）
  {
    id: 'scout-inbox',
    label: 'スカウト受信箱',
    icon: <MailIcon />,
    path: '/messages',
    studentOnly: true,
  },
  {
    id: 'timeline',
    label: 'タイムライン',
    icon: <TimelineIcon />,
    path: '/timeline',
    studentOnly: true,
  },
  {
    id: 'tasks',
    label: 'タスク管理',
    icon: <AssignmentIcon />,
    path: '/tasks',
    studentOnly: true,
  },
  {
    id: 'companies',
    label: '企業一覧',
    icon: <BusinessIcon />,
    path: '/companies',
    studentOnly: true,
    divider: true,
  },
  {
    id: 'mypage',
    label: 'マイページ',
    icon: <PersonIcon />,
    path: '/mypage',
  },
  {
    id: 'events',
    label: 'イベントカレンダー',
    icon: <CalendarMonthIcon />,
    path: '/events',
    studentOnly: true,
  },
  {
    id: 'account-deletion',
    label: 'アカウント退会',
    icon: <DeleteIcon />,
    path: '/account-deletion',
    divider: true,
  },
  // 管理者専用メニュー
  {
    id: 'admin-dashboard',
    label: 'ダッシュボード',
    icon: <DashboardIcon />,
    path: '/admin/dashboard',
    adminOnly: true,
  },
  {
    id: 'experts',
    label: 'エキスパートAI',
    icon: <SmartToyIcon />,
    path: '/admin/ai-management',
    adminOnly: true,
  },
  {
    id: 'users',
    label: 'ユーザー管理',
    icon: <PeopleIcon />,
    path: '/admin/users',
    adminOnly: true,
  },
  {
    id: 'admin-companies',
    label: '企業管理',
    icon: <BusinessIcon />,
    path: '/admin/management/companies',
    adminOnly: true,
  },
  {
    id: 'admin-company-preview',
    label: '企業一覧',
    icon: <BusinessIcon />,
    path: '/admin/companies-preview',
    adminOnly: true,
  },
  {
    id: 'sponsors',
    label: 'スポンサー管理',
    icon: <StarIcon />,
    path: '/admin/sponsors',
    adminOnly: true,
  },
  {
    id: 'analytics',
    label: '分析',
    icon: <AnalyticsIcon />,
    path: '/admin/statistics',
    adminOnly: true,
  },
  {
    id: 'events-admin',
    label: 'イベントカレンダー',
    icon: <CalendarMonthIcon />,
    path: '/events',
    adminOnly: true,
  },
  {
    id: 'notifications',
    label: '通知一覧',
    icon: <NotificationsIcon />,
    path: '/admin/notifications',
    adminOnly: true,
  },
];

const SIDEBAR_WIDTH = 280;

// UserRoleを直接定義（Header.tsxと同様）
const UserRole = {
  STUDENT: 'student' as const,
  ADMIN: 'admin' as const,
} as const;

export const Sidebar: React.FC<SidebarProps> = ({
  open,
  onClose,
  variant = 'temporary',
  currentView,
  title = 'ガクフドキャリア',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { sessions, isLoading, loadChatHistory, updateSession } =
    useChatHistory();
  const isAdminUser = user?.userRole === 'admin';
  const { unreadCount } = useNotificationContext();

  // 表示するチャット履歴の数を決定（段階的表示パターン）
  const initialDisplayCount = 8; // 初期表示: サイドバーに適度に収まる件数
  const incrementalCount = 15; // 追加表示: 意味のある増加量

  const [displayedCount, setDisplayedCount] = useState(initialDisplayCount);

  // サーバーから取得したisPinnedを直接使用
  const sessionsWithPinning = sessions.map((session) => ({
    ...session,
    isPinned: session.isPinned ?? false, // サーバーからのピン留め状態を使用
  }));

  // ピン留めされたチャットを上部に表示するためのソート
  const sortedSessions = [...sessionsWithPinning].sort((a, b) => {
    // ピン留めされたものを上部に
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;

    // 同じピン留め状態の場合は、updatedAtで新しいものを上に
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  // コンテキストメニュー状態
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    sessionId: number;
  } | null>(null);

  // 名前変更ダイアログ状態
  const [renameDialog, setRenameDialog] = useState<{
    open: boolean;
    sessionId: number | null;
    currentName: string;
    newName: string;
  }>({
    open: false,
    sessionId: null,
    currentName: '',
    newName: '',
  });

  // ログアウト確認ダイアログ状態
  const [logoutDialog, setLogoutDialog] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);

  const drawerVariant = isMobile ? 'temporary' : variant;

  // チャット履歴を読み込み（管理者も含む）
  useEffect(() => {
    if (isAuthenticated) {
      loadChatHistory(true);
    }
  }, [isAuthenticated, loadChatHistory]);

  // 権限ベースでメニューをフィルタリング
  const visibleMenuItems = menuItems.filter((item) => {
    if (item.adminOnly) {
      // 管理者専用メニューは管理者のみ表示
      return isAuthenticated && user?.userRole === UserRole.ADMIN;
    }
    if (item.studentOnly) {
      // 学生専用メニューは学生のみ表示
      return isAuthenticated && user?.userRole === UserRole.STUDENT;
    }
    if (item.id === 'account-deletion') {
      // 退会機能は管理者以外のみ表示
      return isAuthenticated && user?.userRole !== UserRole.ADMIN;
    }
    return true; // 一般メニューは常に表示
  });

  const handleMenuClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const handleNewChat = () => {
    // 現在のURLをチェック
    const currentPath = window.location.pathname + window.location.search;

    if (currentPath.includes('/chat')) {
      // 既にチャットページにいる場合は、強制的にリロードしてリセット
      window.location.href = '/chat?reset=true';
    } else {
      // チャットページにいない場合は通常のナビゲーション
      navigate('/chat?reset=true');
    }

    if (isMobile) {
      onClose();
    }
  };

  const handleChatClick = (sessionId: number) => {
    navigate(`/chat/${sessionId}`);
    if (isMobile) {
      onClose();
    }
  };

  // コンテキストメニューを開く
  const handleContextMenu = (event: React.MouseEvent, sessionId: number) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      sessionId,
    });
  };

  // コンテキストメニューを閉じる
  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  // 名前変更ダイアログを開く
  const handleRenameStart = (sessionId: number) => {
    const session = sessionsWithPinning.find((s) => s.id === sessionId);
    if (session) {
      setRenameDialog({
        open: true,
        sessionId,
        currentName: session.title,
        newName: session.title,
      });
    }
    handleContextMenuClose();
  };

  // 名前変更を実行
  const handleRenameSubmit = async () => {
    if (renameDialog.sessionId && renameDialog.newName.trim()) {
      try {
        await updateSession(renameDialog.sessionId, {
          title: renameDialog.newName,
        });
      } catch (error) {
        console.error('Failed to rename chat:', error);
      }
    }
    setRenameDialog({
      open: false,
      sessionId: null,
      currentName: '',
      newName: '',
    });
  };

  // 名前変更をキャンセル
  const handleRenameCancel = () => {
    setRenameDialog({
      open: false,
      sessionId: null,
      currentName: '',
      newName: '',
    });
  };

  // ピン留めを切り替え
  const handleTogglePin = async (sessionId: number) => {
    try {
      const session = sessionsWithPinning.find((s) => s.id === sessionId);
      if (session) {
        // 実際のAPI呼び出し
        await updateSession(sessionId, { isPinned: !session.isPinned });
      }
    } catch (error) {
      console.error('Failed to toggle pin:', error);
    }
    handleContextMenuClose();
  };

  // ログアウト処理
  const handleLogoutClick = () => {
    setLogoutError(null); // エラーをクリア
    setLogoutDialog(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      setIsLoggingOut(true);
      setLogoutError(null); // エラーをクリア
      await logout();
      // ログアウト成功時のみナビゲートとダイアログを閉じる
      navigate('/login');
      if (isMobile) {
        onClose();
      }
      setLogoutDialog(false);
    } catch (error) {
      console.error('Logout failed:', error);
      // エラー時はダイアログは開いたままにして、ユーザーに視覚的フィードバックを提供
      setLogoutError(
        error instanceof Error
          ? error.message
          : 'ログアウト処理中にエラーが発生しました。もう一度お試しください。'
      );
    } finally {
      // ローディング状態のみリセット（エラー時でもローディングは終了）
      setIsLoggingOut(false);
    }
  };

  const handleLogoutCancel = () => {
    setLogoutError(null); // エラーをクリア
    setLogoutDialog(false);
  };

  // プロフィールアイコンクリック処理（ヘッダーから移動）

  // ユーザー役割表示（ヘッダーから移動）
  const getUserRoleDisplay = () => {
    if (!isAuthenticated || !user) return null;

    const isAdmin = user.userRole === UserRole.ADMIN;
    return (
      <Chip
        icon={isAdmin ? <AdminPanelSettingsIcon /> : <PersonIcon />}
        label={isAdmin ? '管理者' : '一般ユーザー'}
        variant="filled"
        size="medium"
        sx={{
          color: 'white',
          border: 'none',
          borderRadius: 3,
          '& .MuiChip-icon': {
            color: 'white',
            marginLeft: 0.5,
          },
          '& .MuiChip-label': {
            paddingLeft: 1,
            paddingRight: 1.5,
          },
          backgroundColor: isAdmin
            ? '#d32f2f' // 管理者: 赤色（視認性良好）
            : '#1976d2', // 一般ユーザー: 青色（視認性良好）
          '&:hover': {
            backgroundColor: isAdmin
              ? '#c62828' // 管理者: より濃い赤
              : '#1565c0', // 一般ユーザー: より濃い青
          },
          fontWeight: 600,
          fontSize: '0.8rem',
          height: 32,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      />
    );
  };

  // ピン留めされたチャットと通常のチャットを分離
  const pinnedChats = sortedSessions.filter((chat) => chat.isPinned);
  const unpinnedChats = sortedSessions.filter((chat) => !chat.isPinned);

  // 段階的表示ロジック
  const displayedPinnedChats = pinnedChats; // 全てのピン留めチャットを表示
  const displayedUnpinnedChats = unpinnedChats.slice(0, displayedCount);
  const remainingChats = unpinnedChats.slice(displayedCount);
  const hasMoreChats = remainingChats.length > 0;

  // 新しいチャットボタンの有効化条件を改善
  // 1. ローディング中は無効
  // 2. ウェルカム画面では無効（まだチャットが開始されていない状態）
  // 3. チャット画面では有効（新しいチャットを開始できる）
  const shouldEnableNewChatButton = !isLoading && currentView === 'chat';

  const drawerContent = (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Sidebar Header with Title and Profile */}
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        {/* Title Row */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: theme.palette.primary.main,
              fontSize: '1.35rem',
              letterSpacing: '0.01em',
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* User Role Display */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          {!isMobile && getUserRoleDisplay()}
        </Box>
      </Box>

      {/* New Chat Button */}
      <Box sx={{ px: 2, pt: 2 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewChat}
          disabled={!shouldEnableNewChatButton}
          sx={{
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: shouldEnableNewChatButton ? 1 : 'none',
            },
            '&.Mui-disabled': {
              backgroundColor: theme.palette.grey[200],
              color: theme.palette.grey[500],
              cursor: 'not-allowed',
            },
          }}
        >
          新しいチャット
        </Button>

        {/* 無効化時のヘルプテキスト */}
        {!shouldEnableNewChatButton && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              color: theme.palette.grey[500],
              mt: 1,
              px: 1,
              fontSize: '0.7rem',
              lineHeight: 1.3,
            }}
          >
            {isLoading
              ? 'チャット履歴を読み込み中...'
              : currentView === 'welcome'
                ? '専門家を選択して\nチャットを開始してください'
                : sessions.length === 0
                  ? 'メッセージを送信すると\n新しいチャットが開始されます'
                  : '新しいチャットを開始できます'}
          </Typography>
        )}
      </Box>

      {/* Recent Chats Section - すべての認証済みユーザー */}
      {isAuthenticated && (
        <Box sx={{ px: 2, py: 2, flexGrow: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              px: 1,
            }}
          >
            最近のチャット
          </Typography>
          <List sx={{ mt: 1 }}>
            {isLoading ? (
              // ローディング状態
              Array.from({ length: 3 }).map((_, index) => (
                <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
                  <Box sx={{ width: '100%', px: 2, py: 1 }}>
                    <Skeleton variant="text" width="80%" height={20} />
                    <Skeleton variant="text" width="40%" height={14} />
                  </Box>
                </ListItem>
              ))
            ) : sessions.length > 0 ? (
              <>
                {/* ピン留めされたチャット */}
                {displayedPinnedChats.length > 0 && (
                  <>
                    {displayedPinnedChats.map((chat) => (
                      <ListItem key={chat.id} disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          onClick={() => handleChatClick(chat.id)}
                          onContextMenu={(e) => handleContextMenu(e, chat.id)}
                          sx={{
                            borderRadius: 1.5,
                            py: 1,
                            px: 2,
                            pr: 1,
                            backgroundColor: theme.palette.primary.main + '04', // 薄い背景色でピン留めを強調
                            '&:hover': {
                              backgroundColor:
                                theme.palette.primary.main + '08',
                            },
                            '&:hover .chat-menu-button': {
                              opacity: 1,
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <PushPinIcon
                              sx={{
                                fontSize: 16,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={chat.title}
                            secondary={formatRelativeTime(chat.updatedAt)}
                            primaryTypographyProps={{
                              fontSize: '0.8rem',
                              fontWeight: 600, // ピン留めは太字
                              noWrap: true,
                            }}
                            secondaryTypographyProps={{
                              fontSize: '0.7rem',
                            }}
                          />
                          {/* コンテキストメニューボタン */}
                          <IconButton
                            className="chat-menu-button"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleContextMenu(e, chat.id);
                            }}
                            sx={{
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              ml: 'auto',
                              p: 0.5,
                            }}
                          >
                            <MoreVertIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </ListItemButton>
                      </ListItem>
                    ))}
                    {/* ピン留めと通常の区切り */}
                    {displayedUnpinnedChats.length > 0 && (
                      <Box sx={{ px: 1, py: 0.5 }}>
                        <Divider sx={{ opacity: 0.5 }} />
                      </Box>
                    )}
                  </>
                )}

                {/* 通常のチャット */}
                {displayedUnpinnedChats.map((chat) => (
                  <ListItem key={chat.id} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => handleChatClick(chat.id)}
                      onContextMenu={(e) => handleContextMenu(e, chat.id)}
                      sx={{
                        borderRadius: 1.5,
                        py: 1,
                        px: 2,
                        pr: 1,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                        '&:hover .chat-menu-button': {
                          opacity: 1,
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <HistoryIcon
                          sx={{
                            fontSize: 16,
                            color: theme.palette.text.secondary,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={chat.title}
                        secondary={formatRelativeTime(chat.updatedAt)}
                        primaryTypographyProps={{
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          noWrap: true,
                        }}
                        secondaryTypographyProps={{
                          fontSize: '0.7rem',
                        }}
                      />
                      {/* コンテキストメニューボタン */}
                      <IconButton
                        className="chat-menu-button"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleContextMenu(e, chat.id);
                        }}
                        sx={{
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          ml: 'auto',
                          p: 0.5,
                        }}
                      >
                        <MoreVertIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </ListItemButton>
                  </ListItem>
                ))}

                {/* もっと見るボタン（段階的表示）*/}
                {hasMoreChats && (
                  <ListItem sx={{ px: 2, pt: 1 }}>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() =>
                        setDisplayedCount((prev) => prev + incrementalCount)
                      }
                      sx={{
                        fontSize: '0.75rem',
                        color: theme.palette.primary.main,
                        textTransform: 'none',
                        minHeight: 'auto',
                        py: 0.5,
                      }}
                    >
                      さらに{Math.min(incrementalCount, remainingChats.length)}
                      件を表示 ({remainingChats.length}件残り)
                    </Button>
                  </ListItem>
                )}
              </>
            ) : (
              // チャット履歴なし
              <ListItem sx={{ px: 2, py: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.75rem' }}
                >
                  まだチャット履歴がありません
                </Typography>
              </ListItem>
            )}
          </List>
        </Box>
      )}

      {/* Navigation Menu */}
      <Box sx={{ px: 2, pb: 2, mt: 'auto' }}>
        <Divider sx={{ mb: 2 }} />
        <List>
          {visibleMenuItems.map((item) => (
            <React.Fragment key={item.id}>
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  selected={location.pathname.startsWith(item.path)}
                  onClick={() => handleMenuClick(item.path)}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main + '08',
                    },
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main + '12',
                      color: theme.palette.primary.main,
                      '& .MuiListItemIcon-root': {
                        color: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {item.id === 'notifications' && isAdminUser ? (
                      <Badge badgeContent={unreadCount > 0 ? unreadCount : undefined} color="error" max={99}>
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
              {item.divider && <Divider sx={{ my: 1, mx: 1 }} />}
            </React.Fragment>
          ))}

          {/* ログアウトボタン */}
          {isAuthenticated && (
            <>
              <Divider sx={{ my: 1, mx: 1 }} />
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={handleLogoutClick}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: theme.palette.error.main + '08',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: theme.palette.error.main,
                    }}
                  >
                    <LogoutIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="ログアウト"
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: theme.palette.error.main,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>
      </Box>
    </Box>
  );

  return (
    <>
      <Drawer
        variant={drawerVariant}
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            border: 'none',
            boxShadow: theme.shadows[2],
            borderRadius: 1,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* コンテキストメニュー */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem
          onClick={() =>
            contextMenu && handleRenameStart(contextMenu.sessionId)
          }
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>名前を変更</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => contextMenu && handleTogglePin(contextMenu.sessionId)}
        >
          <ListItemIcon>
            {sessionsWithPinning.find((s) => s.id === contextMenu?.sessionId)
              ?.isPinned ? (
              <PushPinIcon fontSize="small" />
            ) : (
              <PushPinOutlinedIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {sessionsWithPinning.find((s) => s.id === contextMenu?.sessionId)
              ?.isPinned
              ? '固定を解除'
              : '固定'}
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* 名前変更ダイアログ */}
      <Dialog
        open={renameDialog.open}
        onClose={handleRenameCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>チャット名を変更</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="チャット名"
            fullWidth
            variant="outlined"
            value={renameDialog.newName}
            onChange={(e) =>
              setRenameDialog((prev) => ({ ...prev, newName: e.target.value }))
            }
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleRenameSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRenameCancel}>キャンセル</Button>
          <Button
            onClick={handleRenameSubmit}
            variant="contained"
            disabled={!renameDialog.newName.trim()}
          >
            変更
          </Button>
        </DialogActions>
      </Dialog>

      {/* ログアウト確認ダイアログ */}
      <Dialog open={logoutDialog} onClose={handleLogoutCancel} maxWidth="sm">
        <DialogTitle>ログアウト確認</DialogTitle>
        <DialogContent>
          <Typography>
            本当にログアウトしますか？
            <br />
            現在のセッションが終了し、ログイン画面に戻ります。
          </Typography>
          {logoutError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {logoutError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel} disabled={isLoggingOut}>
            キャンセル
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            color="error"
            disabled={isLoggingOut}
            sx={{
              backgroundColor: theme.palette.error.main,
              '&:hover': {
                backgroundColor: theme.palette.error.dark,
              },
            }}
          >
            {isLoggingOut ? 'ログアウト中...' : 'ログアウト'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Sidebar;
